import { useState } from "react";
import { Trans, useLingui } from "@lingui/react/macro";
import {
  CircleAlert,
  CirclePause,
  Download,
  LogOut,
  MessageSquare,
  MoreHorizontal,
  RefreshCw,
  UploadCloud,
  UserRoundMinus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChangePasswordDialog } from "./ChangePasswordDialog";
import { ResetTwoFactorDialog } from "./ResetTwoFactorDialog";
import { SignOutDevicesDialog } from "./SignOutDevicesDialog";
import { DownloadDataDialog } from "./DownloadDataDialog";
import { ExportActivityLogDialog } from "./ExportActivityLogDialog";
import { ContactSupportDialog } from "./ContactSupportDialog";
import { ReportIssueDialog } from "./ReportIssueDialog";
import { DeactivateAccountDialog } from "./DeactivateAccountDialog";
import { DeleteAccountDialog } from "./DeleteAccountDialog";

type ActiveDialog =
  | "password"
  | "reset-2fa"
  | "sign-out-devices"
  | "download-data"
  | "export-activity"
  | "contact-support"
  | "report-issue"
  | "deactivate"
  | "delete"
  | null;

const itemClass = "gap-3 px-3 py-2.5 text-(--color-text-primary)";
const dangerItemClass =
  "gap-3 px-3 py-2.5 text-(--color-danger-text) focus:text-(--color-danger-text)";

export function ProfileActionsMenu({ email }: { email: string }) {
  const { t } = useLingui();
  const [activeDialog, setActiveDialog] = useState<ActiveDialog>(null);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="brand-outline" className="h-13 shrink-0 gap-2 px-4">
            <MoreHorizontal className="size-5" />
            <span className="text-base font-medium">{t`More`}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-75">
          <DropdownMenuLabel className="font-normal text-(--color-text-secondary)">
            <Trans>Actions</Trans>
          </DropdownMenuLabel>

          <DropdownMenuItem
            className={itemClass}
            onSelect={() => setActiveDialog("password")}
          >
            <RefreshCw />
            <Trans>Change password</Trans>
          </DropdownMenuItem>
          <DropdownMenuItem
            className={itemClass}
            onSelect={() => setActiveDialog("reset-2fa")}
          >
            <RefreshCw />
            <Trans>Reset two-factor authentication</Trans>
          </DropdownMenuItem>
          <DropdownMenuItem
            className={itemClass}
            onSelect={() => setActiveDialog("sign-out-devices")}
          >
            <LogOut />
            <Trans>Sign out from other devices</Trans>
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          <DropdownMenuItem
            className={itemClass}
            onSelect={() => setActiveDialog("download-data")}
          >
            <Download />
            <Trans>Download my data</Trans>
          </DropdownMenuItem>
          <DropdownMenuItem
            className={itemClass}
            onSelect={() => setActiveDialog("export-activity")}
          >
            <UploadCloud />
            <Trans>Export activity</Trans>
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          <DropdownMenuItem
            className={itemClass}
            onSelect={() => setActiveDialog("contact-support")}
          >
            <MessageSquare />
            <Trans>Contact support</Trans>
          </DropdownMenuItem>
          <DropdownMenuItem
            className={itemClass}
            onSelect={() => setActiveDialog("report-issue")}
          >
            <CircleAlert />
            <Trans>Report an issue</Trans>
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          <DropdownMenuItem
            className={dangerItemClass}
            onSelect={() => setActiveDialog("deactivate")}
          >
            <CirclePause />
            <Trans>Deactivate account</Trans>
          </DropdownMenuItem>
          <DropdownMenuItem
            className={dangerItemClass}
            onSelect={() => setActiveDialog("delete")}
          >
            <UserRoundMinus />
            <Trans>Delete account</Trans>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <ChangePasswordDialog
        open={activeDialog === "password"}
        onOpenChange={(open) => setActiveDialog(open ? "password" : null)}
      />
      <ResetTwoFactorDialog
        open={activeDialog === "reset-2fa"}
        onOpenChange={(open) => setActiveDialog(open ? "reset-2fa" : null)}
      />
      <SignOutDevicesDialog
        open={activeDialog === "sign-out-devices"}
        onOpenChange={(open) =>
          setActiveDialog(open ? "sign-out-devices" : null)
        }
      />
      <DownloadDataDialog
        open={activeDialog === "download-data"}
        onOpenChange={(open) => setActiveDialog(open ? "download-data" : null)}
        email={email}
      />
      <ExportActivityLogDialog
        open={activeDialog === "export-activity"}
        onOpenChange={(open) =>
          setActiveDialog(open ? "export-activity" : null)
        }
      />
      <ContactSupportDialog
        open={activeDialog === "contact-support"}
        onOpenChange={(open) =>
          setActiveDialog(open ? "contact-support" : null)
        }
      />
      <ReportIssueDialog
        open={activeDialog === "report-issue"}
        onOpenChange={(open) => setActiveDialog(open ? "report-issue" : null)}
      />
      <DeactivateAccountDialog
        open={activeDialog === "deactivate"}
        onOpenChange={(open) => setActiveDialog(open ? "deactivate" : null)}
      />
      <DeleteAccountDialog
        open={activeDialog === "delete"}
        onOpenChange={(open) => setActiveDialog(open ? "delete" : null)}
      />
    </>
  );
}
